import { create } from "zustand";
import { encrypt, decrypt } from "../utils/encryption";

interface AuthState {
  isAuthenticated: boolean;
  loading: boolean;
  user: any | null;
  role: string | null;
  permissions: Record<string, boolean> | null;
  is_super_admin: boolean;
  checkAuth: () => void;
  login: (token: any) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  isAuthenticated: false,
  loading: true,
  user: null,
  role: null,
  permissions: null,
  is_super_admin: false,

  checkAuth: () => {
    const token =
      typeof window !== "undefined"
        ? localStorage.getItem("accessToken")
        : null;
    const encryptedUser =
      typeof window !== "undefined" ? localStorage.getItem("user") : null;
    const encryptedRole =
      typeof window !== "undefined" ? localStorage.getItem("role") : null;
    const encryptedPermissions =
      typeof window !== "undefined"
        ? localStorage.getItem("permissions")
        : null;
    const refreshToken =
      typeof window !== "undefined"
        ? localStorage.getItem("refreshToken")
        : null;
    const encryptedIsSuperAdmin =
      typeof window !== "undefined"
        ? localStorage.getItem("isSuperAdmin")
        : null;

    // If no user or token, reset state
    if (!encryptedUser || !token) {
      set({ isAuthenticated: false, loading: false, user: null });
      return;
    }

    try {
      // Try to decrypt the user data
      // If decryption fails (returns null) or throws, catch block handles it
      const user = decrypt(encryptedUser);
      const role = encryptedRole ? decrypt(encryptedRole) : null;
      const permissions = encryptedPermissions
        ? decrypt(encryptedPermissions)
        : null;
      const isSuperAdmin = encryptedIsSuperAdmin
        ? decrypt(encryptedIsSuperAdmin)
        : false;

      if (token && user) {
        set({
          isAuthenticated: true,
          loading: false,
          user: user,
          role: role,
          permissions: permissions,
          is_super_admin: isSuperAdmin,
        });
      } else {
        // Fallback for migration: try parsing as plain JSON if decryption returned null
        // This handles cases where user has unencrypted data from before
        try {
          const parsedUser = JSON.parse(encryptedUser);
          if (parsedUser && parsedUser.email) {
            // It was plain JSON, so let's use it (and maybe re-encrypt it on next login)
            // or just log them out to force re-login with encryption.
            // The plan said we invalidate sessions, so let's strict fail here to be safe and clean.
            console.warn("Found unencrypted user data, requiring re-login");
            set({ isAuthenticated: false, loading: false, user: null });
          } else {
            set({ isAuthenticated: false, loading: false, user: null });
          }
        } catch (e) {
          set({ isAuthenticated: false, loading: false, user: null });
        }
      }
    } catch (error) {
      console.error("Failed to parse user:", error);
      set({ isAuthenticated: false, loading: false, user: null });
    }
  },

  login: (payload) => {
    const role = payload.data?.role || null;
    const permissions = payload.data?.permissions || null;
    const is_super_admin = payload.data?.is_super_admin ?? false; // Use ?? to handle false correctly

    if (typeof window !== "undefined") {
      localStorage.setItem("user", encrypt(payload.user));

      if (role) {
        localStorage.setItem("role", encrypt(role));
      } else {
        localStorage.removeItem("role");
      }

      if (permissions) {
        localStorage.setItem("permissions", encrypt(permissions));
      } else {
        localStorage.removeItem("permissions");
      }

      // Always persist is_super_admin status (true or false)
      localStorage.setItem("isSuperAdmin", encrypt(is_super_admin));

      localStorage.setItem("accessToken", payload.accessToken);
      localStorage.setItem("refreshToken", payload.refreshToken);
    }
    set({
      isAuthenticated: true,
      user: payload.user,
      role: role,
      permissions: permissions,
      is_super_admin: is_super_admin,
    });
  },

  logout: () => {
    if (typeof window !== "undefined") {
      localStorage.removeItem("user");
      localStorage.removeItem("role");
      localStorage.removeItem("permissions");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("isSuperAdmin");
    }
    set({ isAuthenticated: false, user: null, role: null, permissions: null, is_super_admin: false });
  },
}));
